import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BidSubmitCard from "../components/BidSubmitCard";
import BidsReceivedCard from "../components/BidsReceivedCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, DollarSign, Clock, User, MessageSquare,
  CheckCircle2, Loader2, Send, AlertCircle, Crown, CreditCard, Banknote, Users, Zap, Target
} from "lucide-react";

const PAYMENT_METHOD_NAMES = {
  cash: "Cash",
  card: "Card",
  paypal: "PayPal",
  venmo: "Venmo",
  zelle: "Zelle",
  cashapp: "Cash App",
  applepay: "Apple Pay",
  googlepay: "Google Pay",
  check: "Check",
  bank_transfer: "Bank Transfer"
};

// Helper function for slots badge styling
const getSlotsStyle = (slotsRemaining) => {
  if (slotsRemaining === 0) return 'bg-red-100 text-red-700';
  if (slotsRemaining === 1) return 'bg-orange-100 text-orange-700';
  return 'bg-green-100 text-green-700';
};

// Helper function for budget display
const formatBudgetRange = (budgetMin, budgetMax) => {
  if (budgetMin && budgetMax) return `$${budgetMin} - $${budgetMax}`;
  if (budgetMax) return `Up to $${budgetMax}`;
  return `From $${budgetMin}`;
};

export default function JobDetailPage() {
  const { jobId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [job, setJob] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [submittingBid, setSubmittingBid] = useState(false);
  
  const [bidForm, setBidForm] = useState({
    message: "",
    proposed_price: "",
    estimated_duration: ""
  });

  const isProfessional = user?.user_type === "professional";
  const isCustomer = user?.user_type === "customer";
  const isSubscribed = user?.subscription_status === "active";
  const isJobOwner = isCustomer && job?.customer_id === user?.id;

  const fetchJobData = useCallback(async () => {
    try {
      let jobRes;
      if (user?.user_type === "professional") {
        jobRes = await axios.get(`${API}/leads/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        jobRes = await axios.get(`${API}/jobs/${jobId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      }
      setJob(jobRes.data);

      if (token) {
        try {
          const bidsRes = await axios.get(`${API}/bids/job/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setBids(bidsRes.data);
        } catch (e) {
          setBids([]);
        }
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || "Subscription required to view lead details");
        navigate("/pricing");
      } else {
        toast.error("Failed to load job details");
        navigate("/pro/dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, [jobId, token, user?.user_type, navigate]);

  useEffect(() => {
    fetchJobData();
  }, [fetchJobData]);

  const handleSubmitBid = async (e) => {
    e.preventDefault();
    
    if (!isSubscribed) {
      toast.error("Subscribe to bid on leads");
      navigate("/pricing");
      return;
    }

    setSubmittingBid(true);
    try {
      await axios.post(
        `${API}/bids`,
        {
          job_id: jobId,
          message: bidForm.message,
          proposed_price: parseFloat(bidForm.proposed_price),
          estimated_duration: bidForm.estimated_duration || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Quote sent successfully!");
      setBidDialogOpen(false);
      setBidForm({ message: "", proposed_price: "", estimated_duration: "" });
      
      // Refresh bids
      const bidsRes = await axios.get(`${API}/bids/job/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBids(bidsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send quote");
    } finally {
      setSubmittingBid(false);
    }
  };

  const handleAcceptBid = async (bidId) => {
    try {
      await axios.patch(
        `${API}/bids/${bidId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Bid accepted! The professional will be notified.");
      
      // Refresh data
      const [jobRes, bidsRes] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}`),
        axios.get(`${API}/bids/job/${jobId}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setJob(jobRes.data);
      setBids(bidsRes.data);
    } catch (error) {
      toast.error("Failed to accept bid");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: "bg-green-100 text-green-700",
      in_progress: "bg-yellow-100 text-yellow-700",
      completed: "bg-blue-100 text-blue-700",
      pending: "bg-yellow-100 text-yellow-700",
      accepted: "bg-green-100 text-green-700"
    };
    return <Badge className={styles[status] || styles.open}>{status.replace("_", " ")}</Badge>;
  };

  const alreadyBid = bids.some(b => b.professional_id === user?.id);

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-forest" />
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="min-h-screen bg-sand">
      <Header />
      
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-5xl mx-auto" data-testid="job-detail-page">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-stone-200">
              <CardContent className="p-6">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {getStatusBadge(job.status)}
                  {job.urgency === "urgent" && (
                    <Badge className="bg-red-100 text-red-700">Urgent</Badge>
                  )}
                  {job.slots_remaining !== undefined && (
                    <Badge className={`flex items-center gap-1 ${getSlotsStyle(job.slots_remaining)}`}>
                      <Users className="w-3 h-3" />
                      {job.already_viewed 
                        ? 'You viewed this lead' 
                        : `${job.slots_remaining} professional slot${job.slots_remaining !== 1 ? 's' : ''} left`}
                    </Badge>
                  )}
                </div>
                
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-gray-900 mb-4" data-testid="job-title">
                  {job.title}
                </h1>
                
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {job.location}
                  </span>
                  {(job.budget_min || job.budget_max) && (
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      {formatBudgetRange(job.budget_min, job.budget_max)}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Posted {new Date(job.created_at).toLocaleDateString()}
                  </span>
                </div>
                
                <div className="prose prose-gray max-w-none">
                  <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-600 whitespace-pre-wrap" data-testid="job-description">{job.description}</p>
                </div>
              </CardContent>
            </Card>

            {/* BQL Express Bidding - Bids Section (for job owner, ONLY when bidding_enabled) */}
            {isJobOwner && job.bidding_enabled && (
              <BidsReceivedCard 
                job={job} 
                onBidAccepted={fetchJobData}
              />
            )}

            {/* Standard Responses Section (for job owner, when bidding NOT enabled) */}
            {isJobOwner && !job.bidding_enabled && (
              <Card className="border-stone-200" data-testid="standard-responses-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-forest" />
                    Responses Received
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {bids.length === 0 ? (
                    <div className="text-center py-8">
                      <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No responses yet. Professionals are reviewing your job.</p>
                      <p className="text-sm text-gray-400 mt-2">Responses typically arrive within 24 hours.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {bids.map((bid) => (
                        <div key={bid.id} className={`p-4 rounded-xl border-2 ${
                          bid.status === "accepted" ? "border-green-400 bg-green-50" : "border-slate-200"
                        }`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <User className="w-4 h-4 text-gray-400" />
                                <span className="font-semibold text-gray-900">
                                  {bid.business_name || bid.professional_name}
                                </span>
                                {bid.status === "accepted" && (
                                  <Badge className="bg-green-500 text-white text-xs">Hired</Badge>
                                )}
                              </div>
                              {bid.message && (
                                <p className="text-sm text-gray-600 mt-2">{bid.message}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-xl font-bold text-forest">${bid.proposed_price}</div>
                              {bid.status === "pending" && (
                                <Button
                                  size="sm"
                                  className="mt-2 bg-green-600 hover:bg-green-700"
                                  onClick={() => handleAcceptBid(bid.id)}
                                  data-testid={`accept-quote-${bid.id}`}
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  Hire
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pricing Type Info */}
            {(job.pricing_type || job.target_price) && (
              <Card className="border-stone-200 bg-gradient-to-br from-slate-50 to-white">
                <CardContent className="p-4">
                  {job.pricing_type === "target_price" && job.target_price ? (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <Target className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Target Price</p>
                        <p className="text-xl font-bold text-green-600">${job.target_price}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Pricing</p>
                        <p className="font-medium text-gray-900">Requesting Quotes</p>
                      </div>
                    </div>
                  )}
                  {job.urgency === "emergency" && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-red-600">
                      <Zap className="w-4 h-4" />
                      <span className="text-sm font-medium">Emergency - {job.max_bids || 3} bid limit</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Customer Info */}
            <Card className="border-stone-200">
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold text-gray-900 mb-4">Posted by</h3>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-forest/10 flex items-center justify-center">
                    <User className="w-6 h-6 text-forest" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{job.customer_name}</p>
                    <p className="text-sm text-gray-500">{job.responses_count || job.bid_count || 0} bids received</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* BQL Express Bidding - Bid Submit Card for Professionals (ONLY when bidding_enabled) */}
            {isProfessional && isSubscribed && job.status === "open" && job.bidding_enabled && (
              <BidSubmitCard 
                job={job} 
                user={user}
                onBidSubmitted={fetchJobData}
              />
            )}

            {/* Standard Quote Request Card for Professionals (when bidding NOT enabled) */}
            {isProfessional && isSubscribed && job.status === "open" && !job.bidding_enabled && (
              <Card className="border-stone-200" data-testid="standard-quote-card">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-forest" />
                    Send a Quote
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    This customer is looking for quotes. Contact them directly to discuss pricing and availability.
                  </p>
                  <Dialog open={bidDialogOpen} onOpenChange={setBidDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="w-full bg-forest hover:bg-forest-dark rounded-full" data-testid="send-quote-btn">
                        <Send className="w-4 h-4 mr-2" />
                        Send Quote
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Send a Quote</DialogTitle>
                        <DialogDescription>
                          Provide your quote details to the customer.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleSubmitBid} className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label>Your Price ($)</Label>
                          <Input
                            type="number"
                            min="1"
                            step="0.01"
                            value={bidForm.proposed_price}
                            onChange={(e) => setBidForm({ ...bidForm, proposed_price: e.target.value })}
                            placeholder="Enter your price"
                            required
                            data-testid="quote-price-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Estimated Duration (optional)</Label>
                          <Input
                            value={bidForm.estimated_duration}
                            onChange={(e) => setBidForm({ ...bidForm, estimated_duration: e.target.value })}
                            placeholder="e.g., 2-3 hours, 1 day"
                            data-testid="quote-duration-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Message to Customer</Label>
                          <Textarea
                            value={bidForm.message}
                            onChange={(e) => setBidForm({ ...bidForm, message: e.target.value })}
                            placeholder="Introduce yourself and explain why you're the best choice..."
                            required
                            data-testid="quote-message-input"
                          />
                        </div>
                        <Button
                          type="submit"
                          className="w-full bg-forest hover:bg-forest-dark"
                          disabled={submittingBid}
                          data-testid="submit-quote-btn"
                        >
                          {submittingBid ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Quote
                            </>
                          )}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            )}

            {/* Subscribe Card for non-subscribed Professionals */}
            {isProfessional && !isSubscribed && (
              <Card className="border-stone-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 text-amber-600 mb-4">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">Subscription Required</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    Subscribe to send bids and access customer details.
                  </p>
                  <Link to="/pricing">
                    <Button className="w-full bg-gradient-to-r from-[#1e3a5f] to-[#2d5a8a] hover:from-[#2d4a6f] hover:to-[#3d6a9a] text-white rounded-full" data-testid="subscribe-to-bid">
                      <CreditCard className="w-4 h-4 mr-2" />
                      View Pricing — Register Free
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Job Closed Card for Professionals */}
            {isProfessional && isSubscribed && job.status !== "open" && (
              <Card className="border-stone-200 bg-gray-50">
                <CardContent className="p-6 text-center">
                  <AlertCircle className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">
                    This job is no longer accepting bids.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Login prompt for non-users */}
            {!user && (
              <Card className="border-stone-200">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-gray-900 mb-4">Want to bid?</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Create a professional account to submit bids.
                  </p>
                  <Link to="/register/professional">
                    <Button className="w-full bg-forest hover:bg-forest-dark rounded-full" data-testid="join-as-pro-btn">
                      Join as Professional
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
